import { SHIPMENT_STATUS_RANK } from "./shipmentBoardStatusLanes.js";

const MAX_RANK = Number.MAX_SAFE_INTEGER;

/**
 * @typedef {{ kind: "status", key: string }} StatusColumn
 * @typedef {{ kind: "milestone", id: number, name?: string, color?: string }} MilestoneColumn
 * @typedef {StatusColumn | MilestoneColumn} CombinedBoardColumn
 */

/**
 * @param {unknown} sequence Saved view sequence from the API.
 * @return {CombinedBoardColumn[]}
 */
export function normalizeCombinedBoardSequence(sequence = []) {
  if (!Array.isArray(sequence)) {
    return [];
  }
  return sequence
    .map(entry => {
      if (!entry || typeof entry !== "object") {
        return null;
      }
      if (entry.kind === "status" && entry.key) {
        return { kind: "status", key: String(entry.key) };
      }
      if (entry.kind === "milestone" && entry.id != null) {
        return { kind: "milestone", id: Number(entry.id) };
      }
      return null;
    })
    .filter(Boolean);
}

/**
 * @param {CombinedBoardColumn[]} columns
 * @param {Array<{ id: number, name: string, meta?: { color?: string } }>} milestoneList
 * @return {CombinedBoardColumn[]}
 */
export function enrichCombinedColumns(columns, milestoneList = []) {
  const byId = Object.fromEntries(milestoneList.map(item => [item.id, item]));
  return columns.map(column => {
    if (column.kind !== "milestone") {
      return column;
    }
    const milestone = byId[column.id];
    return {
      ...column,
      name: milestone?.name || `Milestone ${column.id}`,
      color: milestone?.meta?.color,
    };
  });
}

/**
 * @param {string} statusLabel
 * @return {number}
 */
export function shipmentStatusRank(statusLabel) {
  return SHIPMENT_STATUS_RANK[statusLabel] ?? MAX_RANK;
}

/**
 * @param {number} columnIndex
 * @param {CombinedBoardColumn[]} columns
 * @return {number|null}
 */
export function nextStatusGateRankAfter(
  columnIndex,
  columns,
  statusRank = shipmentStatusRank,
) {
  for (let index = columnIndex + 1; index < columns.length; index += 1) {
    const column = columns[index];
    if (column.kind === "status") {
      return statusRank(column.key);
    }
  }
  return null;
}

/**
 * @param {Object} order
 * @param {CombinedBoardColumn[]} columns
 * @return {string}
 */
export function combinedColumnKeyForOrder(order, columns) {
  const placement = resolveCombinedPlacementColumn(order, columns);
  if (!placement) {
    return "";
  }
  return columnStorageKey(placement);
}

/**
 * @param {CombinedBoardColumn} column
 * @return {string}
 */
export function columnStorageKey(column) {
  if (column.kind === "status") {
    return `status:${column.key}`;
  }
  return `milestone:${column.id}`;
}

/**
 * @param {Object} order
 * @param {CombinedBoardColumn[]} columns
 * @return {CombinedBoardColumn|null}
 */
export function resolveCombinedPlacementColumn(
  order,
  columns,
  statusRank = shipmentStatusRank,
) {
  if (!order || !columns.length) {
    return null;
  }

  const orderRank = statusRank(order.status);
  const milestoneId = order.milestone?.id;

  if (milestoneId != null) {
    const milestoneIndex = columns.findIndex(
      column => column.kind === "milestone" && column.id === milestoneId,
    );
    if (milestoneIndex >= 0) {
      const gateRank = nextStatusGateRankAfter(milestoneIndex, columns, statusRank);
      if (gateRank === null || orderRank < gateRank) {
        return columns[milestoneIndex];
      }
    }
  }

  const exactStatus = columns.find(
    column => column.kind === "status" && column.key === order.status,
  );
  if (exactStatus) {
    return exactStatus;
  }

  let fallback = null;
  let fallbackRank = -1;
  columns.forEach(column => {
    if (column.kind !== "status") {
      return;
    }
    const rank = statusRank(column.key);
    if (rank <= orderRank && rank >= fallbackRank) {
      fallback = column;
      fallbackRank = rank;
    }
  });
  if (fallback) {
    return fallback;
  }

  const firstStatus = columns.find(column => column.kind === "status");
  return firstStatus || null;
}

/**
 * @param {Object} order
 * @param {CombinedBoardColumn} targetColumn
 * @param {CombinedBoardColumn[]} columns
 * @return {boolean}
 */
export function canDropOnCombinedColumn(
  order,
  targetColumn,
  columns,
  statusRank = shipmentStatusRank,
) {
  if (!order || !targetColumn) {
    return false;
  }
  if (targetColumn.kind === "status") {
    return targetColumn.key === order.status && Boolean(order.milestone?.id);
  }
  const milestoneIndex = columns.findIndex(
    column =>
      column.kind === "milestone" && column.id === targetColumn.id,
  );
  if (milestoneIndex < 0) {
    return false;
  }
  const gateRank = nextStatusGateRankAfter(milestoneIndex, columns, statusRank);
  if (gateRank === null) {
    return true;
  }
  return statusRank(order.status) < gateRank;
}

/**
 * @param {Array<Object>} orders
 * @param {CombinedBoardColumn[]} columns
 * @return {Record<string, Array<Object>>}
 */
export function groupShipmentsByCombinedLanes(
  orders = [],
  columns = [],
  statusRank = shipmentStatusRank,
) {
  const groups = {};
  columns.forEach(column => {
    groups[columnStorageKey(column)] = [];
  });

  orders.forEach(order => {
    const placement = resolveCombinedPlacementColumn(order, columns, statusRank);
    if (!placement) {
      return;
    }
    const key = columnStorageKey(placement);
    if (groups[key]) {
      groups[key].push(order);
    }
  });

  return groups;
}

export const COMBINED_BOARD_SHOW_EMPTY_LANES_KEY =
  "shipmentCombinedBoardShowEmptyLanes";

/** @typedef {"none" | "status" | "milestone" | "all"} HideEmptyLanesMode */

export const SHIPMENT_BOARD_HIDE_EMPTY_LANES_MODE_KEY =
  "shipmentBoardHideEmptyLanesMode";

const VALID_HIDE_EMPTY_LANES_MODES = new Set(["none", "status", "milestone", "all"]);

/**
 * @return {boolean}
 */
export function readCombinedBoardShowEmptyLanes() {
  try {
    const raw = localStorage.getItem(COMBINED_BOARD_SHOW_EMPTY_LANES_KEY);
    if (raw === null) {
      return true;
    }
    return raw !== "false";
  } catch (_) {
    return true;
  }
}

/**
 * @param {boolean} showEmptyLanes
 * @return {void}
 */
export function writeCombinedBoardShowEmptyLanes(showEmptyLanes) {
  try {
    localStorage.setItem(
      COMBINED_BOARD_SHOW_EMPTY_LANES_KEY,
      showEmptyLanes ? "true" : "false",
    );
  } catch (_) {
    /* ignore */
  }
}

/**
 * @return {HideEmptyLanesMode}
 */
export function readShipmentBoardHideEmptyLanesMode() {
  try {
    const raw = localStorage.getItem(SHIPMENT_BOARD_HIDE_EMPTY_LANES_MODE_KEY);
    if (raw && VALID_HIDE_EMPTY_LANES_MODES.has(raw)) {
      return /** @type {HideEmptyLanesMode} */ (raw);
    }
    if (readCombinedBoardShowEmptyLanes() === false) {
      return "all";
    }
    return "none";
  } catch (_) {
    return "none";
  }
}

/**
 * @param {HideEmptyLanesMode} mode
 * @return {void}
 */
export function writeShipmentBoardHideEmptyLanesMode(mode) {
  if (!VALID_HIDE_EMPTY_LANES_MODES.has(mode)) {
    return;
  }
  try {
    localStorage.setItem(SHIPMENT_BOARD_HIDE_EMPTY_LANES_MODE_KEY, mode);
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {HideEmptyLanesMode} mode
 * @param {"status" | "milestone"} laneKind
 * @param {number} orderCount
 * @return {boolean}
 */
export function shouldHideEmptyLane(mode, laneKind, orderCount) {
  if (orderCount > 0 || mode === "none") {
    return false;
  }
  if (mode === "all") {
    return true;
  }
  return mode === laneKind;
}

/**
 * @param {string|null|undefined} subType
 * @return {Array<{ value: HideEmptyLanesMode, label: string }>}
 */
const HIDE_EMPTY_LANES_MENU_LABELS = {
  none: "Show all lanes",
  status: "Hide empty status lanes",
  milestone: "Hide empty milestone lanes",
  all: "Hide all empty lanes",
};

export function getHideEmptyLanesMenuOptions(subType) {
  if (subType === "combined") {
    return [
      { value: "none", label: HIDE_EMPTY_LANES_MENU_LABELS.none },
      { value: "status", label: HIDE_EMPTY_LANES_MENU_LABELS.status },
      { value: "milestone", label: HIDE_EMPTY_LANES_MENU_LABELS.milestone },
      { value: "all", label: HIDE_EMPTY_LANES_MENU_LABELS.all },
    ];
  }
  if (subType === "milestone") {
    return [
      { value: "none", label: HIDE_EMPTY_LANES_MENU_LABELS.none },
      { value: "milestone", label: HIDE_EMPTY_LANES_MENU_LABELS.milestone },
    ];
  }
  return [
    { value: "none", label: HIDE_EMPTY_LANES_MENU_LABELS.none },
    { value: "status", label: HIDE_EMPTY_LANES_MENU_LABELS.status },
  ];
}

/**
 * Short pill shown on the toolbar when hiding is active.
 * @param {HideEmptyLanesMode} mode
 * @param {string|null|undefined} subType
 * @return {string|null}
 */
export function getHideEmptyLanesButtonPill(mode, subType) {
  const effective = resolveHideEmptyLanesMenuSelection(mode, subType);
  if (effective === "none") {
    return null;
  }
  if (effective === "milestone") {
    return "milestones";
  }
  return effective;
}

/**
 * @param {HideEmptyLanesMode} mode
 * @param {string|null|undefined} subType
 * @return {string}
 */
export function getHideEmptyLanesAriaLabel(mode, subType) {
  const effective = resolveHideEmptyLanesMenuSelection(mode, subType);
  if (effective === "none") {
    return "Hide empty lanes, off";
  }
  const pill = getHideEmptyLanesButtonPill(mode, subType);
  return `Hide empty lanes, ${pill} hidden`;
}

/**
 * Maps stored mode to the menu value highlighted for the current board sub-type.
 * @param {HideEmptyLanesMode} mode
 * @param {string|null|undefined} subType
 * @return {HideEmptyLanesMode}
 */
export function resolveHideEmptyLanesMenuSelection(mode, subType) {
  if (mode === "all" && subType !== "combined") {
    return subType === "milestone" ? "milestone" : "status";
  }
  return mode;
}

/**
 * @param {HideEmptyLanesMode} mode
 * @return {boolean}
 */
export function isHideEmptyLanesActive(mode) {
  return mode !== "none";
}
